import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { fetchQuizzes, getCourses, getLearners } from '../api';
import ExportModal from './exportmodal';
import QuestionsManager from './QuestionsManager';
import CertificatesManager from './CertificatesManager';
import './Quiz.css';

/* ------------------ Modern Subcomponents ------------------ */

const QuizCard = ({ quiz, isSelected, onSelect, onViewQuestions }) => {
  // Quizzes in the quiz tab are actually courses, so we need to get the course_id
  // The quiz.id should be used as course_id for filtering questions
  const courseId = quiz.id; // In quiz tab, quiz.id is the course_id
  
  return (
  <div 
    className={`quiz-card ${isSelected ? 'selected' : ''}`}
    onClick={() => {
      onSelect(quiz.id);
      // Only open if quiz has questions (courses 1 and 2 have questions)
      if ((quiz.questions_count ?? 0) > 0) {
        onViewQuestions({ ...quiz, id: courseId }); // Pass as course for filtering
      }
    }}
          >
    <div className="quiz-card-header">
      <div className="quiz-card-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      </div>
      <h3 className="quiz-card-title">{quiz.title ?? 'Untitled Quiz'}</h3>
    </div>
    <div className="quiz-card-body">
      <div className="quiz-stat">
        <span className="quiz-stat-label">Questions</span>
        <span className="quiz-stat-value">{quiz.questions_count ?? 0}</span>
      </div>
      <div className="quiz-stat">
        <span className="quiz-stat-label">Avg Score</span>
        <span className="quiz-stat-value">
          {quiz.tests_avg_percentile != null ? `${quiz.tests_avg_percentile.toFixed(1)}%` : '—'}
        </span>
      </div>
      <div className="quiz-stat">
        <span className="quiz-stat-label">Attempts</span>
        <span className="quiz-stat-value">{quiz.completed ?? 0}</span>
      </div>
  </div>
    {(quiz.questions_count ?? 0) > 0 && (
      <div className="quiz-card-footer">
        <button 
          className="quiz-card-action-btn"
          onClick={(e) => {
            e.stopPropagation();
            // Pass quiz as course for filtering by course_id
            onViewQuestions({ ...quiz, id: courseId });
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <path d="M12 17h.01" />
          </svg>
          View Questions
          </button>
        </div>
    )}
  </div>
);
};

const CourseCard = ({ course, isSelected, onSelect, onViewQuestions, onViewCertificates, isCertificationTab }) => {
  const hasQuestions = (course.questions_count ?? 0) > 0;
  const hasCompletions = (course.completed ?? 0) > 0;

  return (
  <div 
    className={`course-card ${isSelected ? 'selected' : ''} ${!hasCompletions && isCertificationTab ? 'no-completions' : ''}`}
    onClick={() => {
      onSelect(course);
      // In certification tab, open certificates; in quiz tab, open questions
      if (isCertificationTab && hasCompletions) {
        onViewCertificates(course);
      } else if (!isCertificationTab && hasQuestions) {
        onViewQuestions(course);
      }
    }}
  >
    <div className="course-card-image">
      <img src={course.image_url || '/fallback.jpg'} alt={course.title} />
      <div className="course-card-badge">
        {(course.completed ?? 0) > 0 ? (
          <span className="badge-success">{course.completed} Completed</span>
        ) : (
          <span className="badge-empty">No one completed yet</span>
        )}
      </div>
    </div>
    <div className="course-card-content">
      <h3 className="course-card-title">{course.title}</h3>
      <p className="course-card-description">
                    {course.description || 'No description provided.'}
                  </p>
      <div className="course-card-stats">
        <div className="course-stat-item">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <span>{course.completed ?? 0} Completed</span>
        </div>
        <div className="course-stat-item">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <span>{course.pending ?? 0} Pending</span>
        </div>
        <div className={`course-stat-item status ${course.is_published ? 'published' : 'draft'}`}>
          <span className="status-dot"></span>
          <span>{course.is_published ? 'Published' : 'Draft'}</span>
        </div>
      </div>
    </div>
    {isCertificationTab ? (
      hasCompletions && (
        <div className="course-card-action">
          <button 
            className="course-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              onViewCertificates(course);
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            View Certificates
          </button>
  </div>
      )
    ) : (
      hasQuestions && (
        <div className="course-card-action">
          <button 
            className="course-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              onViewQuestions(course);
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <path d="M12 17h.01" />
            </svg>
            View Questions
          </button>
        </div>
      )
    )}
  </div>
);
};

/* ------------------ Main Component ------------------ */

const Quiz = () => {
  const [activeTab, setActiveTab] = useState('quiz');
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [quizzes, setQuizzes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [learners, setLearners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showQuestionsManager, setShowQuestionsManager] = useState(false);
  const [showCertificatesManager, setShowCertificatesManager] = useState(false);
  const [selectedQuizForQuestions, setSelectedQuizForQuestions] = useState(null);
  const [selectedCourseForQuestions, setSelectedCourseForQuestions] = useState(null);
  const [selectedCourseForCertificates, setSelectedCourseForCertificates] = useState(null);

  useEffect(() => {
    const fetchCertificationData = async () => {
      setLoading(true);
      try {
        const coursesData = await getCourses();
        const coursesArray = Array.isArray(coursesData) ? coursesData : (coursesData?.data ?? []);

        // getCourses() already returns `completed` and `learners_count` per
        // course (backend-computed via withCount) — no need to refetch and
        // recompute them by hitting /enrollments once per course here.
        setCourses(coursesArray);
      } catch (err) {
        console.error('Error fetching certification data:', err);
        setCourses([]);
      } finally {
        setLoading(false);
      }
    };

    if (activeTab === 'quiz') {
      setLoading(true);
      fetchQuizzes()
        .then((res) => setQuizzes(res.data ?? []))
        .finally(() => setLoading(false));
    } else {
      fetchCertificationData();
    }
  }, [activeTab]);

  const filteredQuizzes = useMemo(
    () =>
      quizzes.filter((q) =>
        (q.title || '').toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [quizzes, searchTerm]
  );

  const filteredCourses = useMemo(
    () =>
      courses.filter((c) =>
        (c.title || '').toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [courses, searchTerm]
  );

  return (
    <div className="quiz-page-modern">
      {/* Header */}
      <div className="quiz-page-header">
        <div className="header-content">
          <div className="header-title-section">
        <h1>Quiz & Certification</h1>
            <p className="header-subtitle">Manage quizzes, questions, and certifications</p>
          </div>
          <div className="header-actions">
            <button 
              onClick={() => setShowExportModal(true)}
              className="btn-modern btn-secondary"
              title="Export data"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Export
            </button>
            <Link to="/quiz/set" className="btn-modern btn-primary">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Create Quiz
            </Link>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="quiz-tabs-modern">
        <button
          className={`tab-modern ${activeTab === 'quiz' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('quiz');
            setSearchTerm('');
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
          Quizzes
          <span className="tab-badge">{quizzes.length}</span>
        </button>
        <button
          className={`tab-modern ${activeTab === 'certification' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('certification');
            setSearchTerm('');
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          Certification
          <span className="tab-badge">{courses.length}</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="quiz-search-modern">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
                </svg>
              <input
                type="text"
          placeholder={activeTab === 'quiz' ? 'Search quizzes...' : 'Search courses...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input-modern"
              />
        {searchTerm && (
          <button
            className="search-clear-btn"
            onClick={() => setSearchTerm('')}
            aria-label="Clear search"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
            </div>

      {/* Content */}
      <div className="quiz-content-modern">
        {loading ? (
          <div className="loading-state">
            <div className="spinner-modern"></div>
            <p>Loading {activeTab === 'quiz' ? 'quizzes' : 'courses'}...</p>
          </div>
        ) : activeTab === 'quiz' ? (
          <>
            {filteredQuizzes.length === 0 ? (
              <div className="empty-state">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <h3>{searchTerm ? 'No quizzes match your search' : 'No quizzes found'}</h3>
                <p>{searchTerm ? 'Try a different search term' : 'Create your first quiz to get started'}</p>
                <Link to="/quiz/set" className="btn-modern btn-primary">
                  Create Quiz
                </Link>
              </div>
            ) : (
              <div className="quiz-grid">
                {filteredQuizzes.map((quiz) => (
                  <QuizCard
                    key={quiz.id}
                    quiz={quiz}
                    isSelected={selectedQuiz === quiz.id}
                    onSelect={setSelectedQuiz}
                    onViewQuestions={(quiz) => {
                      // In quiz tab, quizzes are courses, so use quiz.id as courseId
                      setSelectedCourseForQuestions({ ...quiz, id: quiz.id });
                      setShowQuestionsManager(true);
                    }}
              />
                ))}
            </div>
          )}
          </>
        ) : (
          <>
            {filteredCourses.length === 0 ? (
              <div className="empty-state">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
                <h3>{searchTerm ? 'No courses match your search' : 'No courses found'}</h3>
                <p>{searchTerm ? 'Try a different search term' : 'No courses available'}</p>
        </div>
            ) : (
              <div className="course-grid">
                {filteredCourses.map((course) => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    isSelected={selectedCourse?.id === course.id}
                    onSelect={setSelectedCourse}
                    isCertificationTab={true}
                    onViewQuestions={(course) => {
                      // Only open if course has questions (for quiz tab)
                      if ((course.questions_count ?? 0) > 0) {
                        setSelectedCourseForQuestions(course);
                        setShowQuestionsManager(true);
                      }
                    }}
                    onViewCertificates={(course) => {
                      // Open certificates for completed learners
                      setSelectedCourseForCertificates(course);
                      setShowCertificatesManager(true);
                    }}
                  />
                ))}
        </div>
      )}
          </>
        )}
      </div>

      {/* Modals */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        context={{
          type: activeTab === 'quiz' ? 'quizzes' : 'certification',
          quizzes: activeTab === 'quiz' ? filteredQuizzes : [],
          courses: activeTab === 'certification' ? filteredCourses : [],
          learners: activeTab === 'certification' ? learners : []
        }}
      />

      <QuestionsManager
        isOpen={showQuestionsManager}
        onClose={() => {
          setShowQuestionsManager(false);
          setSelectedQuizForQuestions(null);
          setSelectedCourseForQuestions(null);
        }}
        courseId={selectedCourseForQuestions?.id}
        courseTitle={selectedCourseForQuestions?.title}
        quizId={selectedQuizForQuestions?.id}
        quizTitle={selectedQuizForQuestions?.title}
      />

      <CertificatesManager
        isOpen={showCertificatesManager}
        onClose={() => {
          setShowCertificatesManager(false);
          setSelectedCourseForCertificates(null);
        }}
        courseId={selectedCourseForCertificates?.id}
        courseTitle={selectedCourseForCertificates?.title}
      />
    </div>
  );
};

export default Quiz;
